import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reviewAssignmentAPI, evaluationAPI } from '../../api/services';

const EvaluationForm = lazy(() => import('./EvaluationForm'));
const ViewEvaluation = lazy(() => import('./ViewEvaluation'));

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';

const fmtDate = (d?: string) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const relTime = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 0) return `in ${-days}d`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return fmtDate(d);
};

type Tab = 'reviews' | 'evaluations';

const statusStyle = (status: string) => {
  switch (status) {
    case 'completed': case 'submitted': return 'bg-green-100 text-green-700';
    case 'in_progress': case 'draft': return 'bg-blue-100 text-blue-700';
    case 'overdue': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const EvaluationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'reviews');

  const [assignments, setAssignments] = useState<any[]>([]);
  const [assignLoading, setAssignLoading] = useState(true);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [evalLoading, setEvalLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state: 'fill' for EvaluationForm, 'view' for ViewEvaluation
  const [modalType, setModalType] = useState<'fill' | 'view' | null>(
    searchParams.get('evaluationId') ? 'view' : searchParams.get('assignmentId') ? 'fill' : null
  );

  const openFill = (params: string) => {
    setModalType('fill');
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      const p = new URLSearchParams(params);
      p.forEach((v, k) => n.set(k, v));
      n.set('tab', tab);
      return n;
    }, { replace: true });
  };

  const openView = (evalId: string) => {
    setModalType('view');
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('evaluationId', evalId);
      n.set('tab', tab);
      return n;
    }, { replace: true });
  };

  const closeModal = () => {
    setModalType(null);
    setSearchParams({ tab }, { replace: true });
    // Refresh data
    reviewAssignmentAPI.getUserReviewAssignments().then(res => {
      setAssignments(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
    evaluationAPI.getMyEvaluations().then(res => {
      setEvaluations(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  };

  useEffect(() => {
    reviewAssignmentAPI.getUserReviewAssignments().then(res => {
      setAssignments(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
    }).catch(() => {}).finally(() => setAssignLoading(false));
  }, []);

  useEffect(() => {
    evaluationAPI.getMyEvaluations().then(res => {
      setEvaluations(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);
    }).catch(() => {}).finally(() => setEvalLoading(false));
  }, []);

  useEffect(() => { setSearchParams({ tab }, { replace: true }); }, [tab]);

  const pendingAssignments = assignments.filter(a => !a.isCompleted && a.evaluationStatus !== 'submitted');
  const completedAssignments = assignments.filter(a => a.isCompleted || a.evaluationStatus === 'submitted');

  const filterBySearch = (items: any[], fields: string[]) => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter(item => fields.some(f => {
      const v = f.split('.').reduce((o: any, k) => o?.[k], item);
      return typeof v === 'string' && v.toLowerCase().includes(s);
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Evaluations</h1>
        <p className="text-sm text-gray-400 mt-0.5">Performance reviews and self-evaluations</p>
      </div>

      {/* Search + Tabs */}
      <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
        <div className="w-64 shrink-0">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex-1 flex justify-center gap-0.5">
          {([
            { key: 'reviews' as const, label: 'Open Reviews', count: pendingAssignments.length },
            { key: 'evaluations' as const, label: 'My Evaluations', count: evaluations.length },
          ]).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                tab === t.key ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'
              }`}>
              {t.label} {t.count > 0 && <span className="text-gray-400 ml-0.5">{t.count}</span>}
            </button>
          ))}
        </div>
        <div className="w-64 shrink-0" />
      </div>

      {/* ── Open Reviews ──────────────────────────── */}
      {tab === 'reviews' && (
        <div>
          {assignLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : pendingAssignments.length === 0 && completedAssignments.length === 0 ? (
            <p className="text-center py-12 text-sm text-gray-400">No review assignments</p>
          ) : (
            <>
              {/* Column header */}
              {(pendingAssignments.length > 0 || completedAssignments.length > 0) && (
                <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
                  <div className="col-span-4 flex items-center gap-1">
                    <span className="text-gray-300">Aa</span> Evaluation
                  </div>
                  <div className="col-span-2">Period</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2">Due</div>
                  <div className="col-span-1">Instructions</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>
              )}

              {/* Pending */}
              {pendingAssignments.length > 0 && (
                <>
                  <div className="px-6 py-1.5 border-b border-gray-100">
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Pending ({pendingAssignments.length})</span>
                  </div>
                  {filterBySearch(pendingAssignments, ['evaluationType.name', 'evaluationTypeName', 'evaluationPeriod']).map((a: any) => {
                    const isOverdue = a.dueDate && new Date(a.dueDate) < new Date();
                    const isDueSoon = a.dueDate && !isOverdue && (new Date(a.dueDate).getTime() - Date.now()) < 7 * 86400000;
                    return (
                      <div key={a._id}
                        className={`grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 hover:bg-gray-50/80 cursor-pointer transition-colors ${isOverdue ? 'border-l-2 border-l-red-400' : isDueSoon ? 'border-l-2 border-l-yellow-400' : ''}`}
                        onClick={() => {
                          const typeId = typeof a.evaluationTypeId === 'object' ? a.evaluationTypeId?._id : a.evaluationTypeId;
                          const params = [`assignmentId=${a._id}`, `period=${encodeURIComponent(a.evaluationPeriod || '')}`, `typeId=${typeId || ''}`];
                          if (a.evaluationId) params.push(`evaluationId=${a.evaluationId}`);
                          openFill(params.join('&'));
                        }}>
                        <div className="col-span-4 flex items-center gap-2 min-w-0">
                          <span className="text-sm text-gray-700 truncate capitalize">{(a as any).evaluationType?.name || a.evaluationTypeName || 'Evaluation'}</span>
                        </div>
                        <div className="col-span-2 flex items-center text-xs text-gray-500">{a.evaluationPeriod || '—'}</div>
                        <div className="col-span-1 flex items-center">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusStyle(a.status)}`}>{cap(a.status || 'not started')}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          {a.dueDate ? <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{relTime(a.dueDate)}</span> : <span className="text-xs text-gray-300">—</span>}
                        </div>
                        <div className="col-span-1 flex items-center">
                          {a.instructions ? <span className="text-[10px] text-gray-400 truncate" title={a.instructions}>{a.instructions.length > 20 ? a.instructions.slice(0, 20) + '…' : a.instructions}</span> : <span className="text-xs text-gray-300">—</span>}
                        </div>
                        <div className="col-span-2 flex items-center justify-end">
                          <span className="text-xs text-blue-600">{a.evaluationStatus === 'draft' ? 'Continue' : 'Start'} →</span>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Completed */}
              {completedAssignments.length > 0 && (
                <>
                  <div className="px-6 py-1.5 border-b border-gray-100">
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Completed ({completedAssignments.length})</span>
                  </div>
                  {filterBySearch(completedAssignments, ['evaluationType.name', 'evaluationTypeName', 'evaluationPeriod']).map((a: any) => (
                    <div key={a._id}
                      className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 hover:bg-gray-50/80 cursor-pointer transition-colors"
                      onClick={() => a.evaluationId ? openView(a.evaluationId) : null}>
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <svg className="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        <span className="text-sm text-gray-700 truncate capitalize">{a.evaluationType?.name || a.evaluationTypeName || 'Evaluation'}</span>
                      </div>
                      <div className="col-span-2 flex items-center text-xs text-gray-500">{a.evaluationPeriod || '—'}</div>
                      <div className="col-span-1 flex items-center">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">Completed</span>
                      </div>
                      <div className="col-span-2 flex items-center text-xs text-gray-400">{a.completedAt ? relTime(a.completedAt) : '—'}</div>
                      <div className="col-span-1" />
                      <div className="col-span-2 flex items-center justify-end">
                        <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── My Evaluations ────────────────────────── */}
      {tab === 'evaluations' && (
        <div>
          {/* Column header */}
          {!evalLoading && evaluations.length > 0 && (
            <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
              <div className="col-span-3">Period</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-1">Level</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Score</div>
              <div className="col-span-2">Rating</div>
              <div className="col-span-1">Updated</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
          )}

          {evalLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : evaluations.length === 0 ? (
            <p className="text-center py-12 text-sm text-gray-400">No evaluations yet</p>
          ) : filterBySearch(evaluations, ['evaluationPeriod', 'levelGroup', 'performanceRating']).map((ev: any) => {
            const score = ev.totalScore ?? ev.averageScore ?? 0;
            const isSubmitted = ev.status === 'submitted';
            return (
              <div key={ev._id}
                className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 hover:bg-gray-50/80 cursor-pointer transition-colors"
                onClick={() => {
                  if (isSubmitted) { openView(ev._id); return; }
                  const params = [`evaluationId=${ev._id}`];
                  if (ev.evaluationTypeId) params.push(`typeId=${typeof ev.evaluationTypeId === 'object' ? ev.evaluationTypeId._id : ev.evaluationTypeId}`);
                  if (ev.assignmentId) params.push(`assignmentId=${typeof ev.assignmentId === 'object' ? ev.assignmentId._id : ev.assignmentId}`);
                  if (ev.evaluationPeriod) params.push(`period=${encodeURIComponent(ev.evaluationPeriod)}`);
                  openFill(params.join('&'));
                }}>
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  {isSubmitted && <svg className="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                  {!isSubmitted && <div className={`w-2 h-2 rounded-full shrink-0 ${ev.status === 'draft' ? 'bg-blue-400' : 'bg-gray-400'}`} />}
                  <span className="text-sm text-gray-700 truncate">{ev.evaluationPeriod || 'Evaluation'}</span>
                </div>
                <div className="col-span-2 flex items-center text-xs text-gray-500 truncate">{typeof ev.evaluationTypeId === 'object' ? ev.evaluationTypeId?.name : (ev.evaluationTypeName || '—')}</div>
                <div className="col-span-1 flex items-center text-xs text-gray-500">{ev.levelGroup || '—'}</div>
                <div className="col-span-1 flex items-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusStyle(ev.status)}`}>{cap(ev.status || 'draft')}</span>
                </div>
                <div className="col-span-1 flex items-center">
                  {score > 0 ? <span className="text-xs text-blue-600 font-medium">{typeof score === 'number' ? score.toFixed(1) : score}</span> : <span className="text-xs text-gray-300">—</span>}
                </div>
                <div className="col-span-2 flex items-center text-xs text-gray-500">{ev.performanceRating || '—'}</div>
                <div className="col-span-1 flex items-center text-xs text-gray-400">{fmtDate(ev.updatedAt || ev.createdAt)}</div>
                <div className="col-span-1 flex items-center justify-end">
                  <span className="text-xs text-blue-600">{isSubmitted ? 'View' : 'Continue'} →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Evaluation modal */}
      {modalType && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-200 shrink-0">
              <span className="text-xs text-gray-400">{modalType === 'view' ? 'View Evaluation' : 'Fill Evaluation'}</span>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>}>
                {modalType === 'fill' && <EvaluationForm />}
                {modalType === 'view' && <ViewEvaluation />}
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationsPage;
